import { createLoader } from '@meteor/data-hooks';
import { isServiceError } from '@meteor/error';
import { adminClient } from './client';

let redirectToAuthIfNotAuthenticated = async <R>(fn: () => Promise<R>) => {
  if (typeof window === 'undefined') new Promise(() => {}) as Promise<R>;

  try {
    return await fn();
  } catch (err) {
    if (isServiceError(err) && err.data.code == 'unauthorized') {
      window.location.replace('/login');

      // Noop promise to stop execution while redirecting
      return new Promise(() => {}) as Promise<R>;
    }

    throw err;
  }
};

export let usersState = createLoader({
  name: 'users',
  fetch: (d: { search?: string; after?: string }) => {
    return redirectToAuthIfNotAuthenticated(() =>
      adminClient.admin.listUsers({
        search: d.search,
        after: d.after
      })
    );
  },
  mutators: {}
});

export let userState = createLoader({
  name: 'user',
  fetch: (d: { id: string }) => {
    return redirectToAuthIfNotAuthenticated(() =>
      adminClient.admin.getUser({
        id: d.id
      })
    );
  },
  mutators: {
    impersonate: (d: { reason: string; password: string }, { output }) => {
      return redirectToAuthIfNotAuthenticated(() =>
        adminClient.admin.impersonateUser({
          id: output.id,
          password: d.password,
          reason: d.reason
        })
      );
    }
  }
});

export let adminAgentsState = createLoader({
  name: 'agents',
  fetch: (d: { search?: string; after?: string }) => {
    return redirectToAuthIfNotAuthenticated(() =>
      adminClient.admin.listAdminAgents({
        search: d.search,
        after: d.after
      })
    );
  },
  mutators: {
    create: (d: { agentId: string; name: string; description: string; slug: string }) => {
      return redirectToAuthIfNotAuthenticated(() =>
        adminClient.admin.createAgentTemplate({
          agentId: d.agentId,
          name: d.name,
          description: d.description,
          slug: d.slug
        })
      );
    }
  }
});

export let agentTemplateState = createLoader({
  name: 'agentTemplate',
  fetch: (d: { id: string }) => {
    return redirectToAuthIfNotAuthenticated(() => adminClient.admin.getAgentTemplate(d));
  },
  mutators: {}
});

export let organizationsState = createLoader({
  name: 'organizations',
  fetch: (d: { search?: string; after?: string }) => {
    return redirectToAuthIfNotAuthenticated(() =>
      adminClient.admin.listOrganizations({
        search: d.search,
        after: d.after
      })
    );
  },
  mutators: {}
});

export let organizationState = createLoader({
  name: 'organization',
  fetch: (d: { id: string }) => {
    return redirectToAuthIfNotAuthenticated(() =>
      adminClient.admin.getOrganization({
        id: d.id
      })
    );
  },
  mutators: {}
});

export let adminsState = createLoader({
  name: 'admins',
  fetch: (d: { search?: string; after?: string }) => {
    return redirectToAuthIfNotAuthenticated(() =>
      adminClient.admin.listAdmins({
        search: d.search,
        after: d.after
      })
    );
  },
  mutators: {}
});

export let authConfigState = createLoader({
  name: 'authConfig',
  fetch: (d: void) => {
    return redirectToAuthIfNotAuthenticated(() => adminClient.admin.getAuthConfig({}));
  },
  mutators: {
    update: (d: { hasWhitelist: boolean }) => {
      return redirectToAuthIfNotAuthenticated(() =>
        adminClient.admin.updateAuthConfig({
          hasWhitelist: d.hasWhitelist
        })
      );
    }
  }
});

export let invitesState = createLoader({
  name: 'invites',
  fetch: (d: { search?: string; after?: string }) => {
    return redirectToAuthIfNotAuthenticated(() =>
      adminClient.admin.listInvites({
        search: d.search,
        after: d.after
      })
    );
  },
  mutators: {
    create: (d: { email: string; title?: string; message?: string }) => {
      return redirectToAuthIfNotAuthenticated(() =>
        adminClient.admin.createInvite({
          email: d.email,
          title: d.title,
          message: d.message
        })
      );
    }
  }
});

export let plansState = createLoader({
  name: 'plans',
  fetch: (d: void) => {
    return redirectToAuthIfNotAuthenticated(() => adminClient.admin.listPlans({}));
  },
  mutators: {}
});

export let organizationPlanState = createLoader({
  name: 'orgPlan',
  fetch: (d: { organizationId: string }) => {
    return redirectToAuthIfNotAuthenticated(() => adminClient.admin.getOrganizationPlan(d));
  },
  mutators: {
    setOrganizationPlan: (d: { organizationId: string; planId: string }) => {
      return redirectToAuthIfNotAuthenticated(() =>
        adminClient.admin.setOrganizationPlan({
          organizationId: d.organizationId,
          planId: d.planId
        })
      );
    }
  }
});

export let earlyAccessState = createLoader({
  name: 'earlyAccess',
  fetch: (d: { search?: string; after?: string }) => {
    return redirectToAuthIfNotAuthenticated(() =>
      adminClient.admin.listEarlyAccessRegistrations({
        search: d.search,
        after: d.after
      })
    );
  },
  mutators: {}
});
